import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Copy, Plus, Sparkles } from 'lucide-react'
import GenericFormError from 'app/frontend/base/components/GenericFormError'
import Card from 'app/frontend/base/components/Card'
import Select from 'app/frontend/base/components/Select'
import { awaitPendingJob, displayErrorToast } from 'app/frontend/base/helpers'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createTeam, duplicateTeam } from 'app/frontend/base/api/team'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { PREMIUM_BENEFIT_CONFIG } from 'app/frontend/types/custom/PremiumBenefitCategory'

const formSchema = z.object({
  name: z.string().min(2).max(20),
})

type Mode = 'empty' | 'duplicate'

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
}

const NewTeamModal = ({ open, setOpen }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const [mode, setMode] = useState<Mode>()
  const [sourceTeamId, setSourceTeamId] = useState<string>()
  const [sourceError, setSourceError] = useState(false)
  const { t } = useTranslation()
  const { guild, guildReference, currentTeam } = useGuild()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema> & { genericError?: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: guild?.teamReferences?.length ? '' : 'Main',
    },
  })

  const teamLimitReached =
    !guild || guild.teamLimit <= guild.teamReferences?.length
  const atMaxLimit = guild?.teamLimit === PREMIUM_BENEFIT_CONFIG.team_limit.max

  const resetState = () => {
    setMode(undefined)
    setSourceTeamId(undefined)
    setSourceError(false)
    setError(undefined)
    setSubmitting(false)
    form.reset()
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState()
    setOpen(next)
  }

  const onSuccess = (
    team: wowaudit.TeamReference,
    createdGuild: wowaudit.GuildReference,
  ) => {
    toast.success(t('dashboard.team.created-message'))
    handleOpenChange(false)

    queryClient.invalidateQueries({
      queryKey: ['guild', { path: guildReference?.path }],
    })

    navigate(team.path, {
      state: {
        guildReference: createdGuild,
        teamReference: team,
      },
    })
  }

  const startSetup = () => {
    if (!guildReference) return
    handleOpenChange(false)
    navigate(`${guildReference.path}/onboarding?step=team`)
  }

  const createEmpty = (values: z.infer<typeof formSchema>) => {
    if (!guildReference) return

    setError(undefined)
    setSubmitting(true)
    createTeam(guildReference.id, values)
      .then((response) => {
        onSuccess(response.team, response.guild)
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmitting(false)
        setError(response.response.data.message)
      })
  }

  const createDuplicate = (values: z.infer<typeof formSchema>) => {
    if (!guildReference) return
    if (!sourceTeamId) {
      setSourceError(true)
      return
    }

    setError(undefined)
    setSubmitting(true)
    duplicateTeam(guildReference.id, {
      sourceTeamId: Number(sourceTeamId),
      name: values.name,
    })
      .then((response) => {
        awaitPendingJob<{
          team: wowaudit.TeamReference
          guild: wowaudit.GuildReference
        }>(response.job_id, (result) => {
          if (result.error || !result.content) {
            setSubmitting(false)
            setError(result.error)
            return
          }

          onSuccess(result.content.team, result.content.guild)
        })
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmitting(false)
        setError(response.response.data.message)
      })
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (mode === 'duplicate') return createDuplicate(values)
    createEmpty(values)
  }

  const options = [
    {
      mode: 'setup' as const,
      icon: Sparkles,
      title: t('dashboard.team.create-options.setup-title'),
      caption: t('dashboard.team.create-options.setup-caption'),
      onSelect: startSetup,
      active: false,
    },
    {
      mode: 'duplicate' as const,
      icon: Copy,
      title: t('dashboard.team.create-options.duplicate-title'),
      caption: t('dashboard.team.create-options.duplicate-caption'),
      onSelect: () => setMode('duplicate'),
      active: mode === 'duplicate',
    },
    {
      mode: 'empty' as const,
      icon: Plus,
      title: t('dashboard.team.create-options.empty-title'),
      caption: t('dashboard.team.create-options.empty-caption'),
      onSelect: () => setMode('empty'),
      active: mode === 'empty',
    },
  ]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t('dashboard.team.create-new')}</DialogTitle>
        </DialogHeader>

        {teamLimitReached && atMaxLimit && (
          <Alert variant='info'>
            <AlertTitle>{t('dashboard.team.team-limit-reached')}</AlertTitle>
            <AlertDescription>
              {t('dashboard.team.team-limit-reached-description-max', {
                limit: guild?.teamLimit,
              })}
            </AlertDescription>
          </Alert>
        )}
        {teamLimitReached && !atMaxLimit && (
          <PremiumUpsell
            title={t('dashboard.team.team-limit-reached')}
            description={t('dashboard.team.team-limit-reached-description')}
            to={`${
              currentTeam?.path ?? guild?.teamReferences?.[0]?.path
            }/premium`}
            onNavigate={() => handleOpenChange(false)}
          />
        )}

        <div className='grid grid-cols-3 gap-2'>
          {options.map((option) => (
            <Card
              key={option.mode}
              role='button'
              tabIndex={teamLimitReached ? -1 : 0}
              onClick={() => {
                if (teamLimitReached) return
                option.onSelect()
              }}
              className={cn(
                'flex flex-col items-center gap-2 p-3 text-center transition-colors',
                teamLimitReached
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer hover:border-primary',
                option.active && 'border-primary',
              )}
            >
              <span className='flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary'>
                <option.icon className='h-4 w-4' />
              </span>
              <span className='text-sm font-medium'>{option.title}</span>
              <span className='text-xs text-muted-foreground'>
                {option.caption}
              </span>
            </Card>
          ))}
        </div>

        {mode ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              id='new-team-form'
              className='flex flex-col gap-4'
            >
              {mode === 'duplicate' && (
                <div className='flex flex-col gap-2'>
                  <FormLabel>
                    {t('dashboard.team.create-options.source-team')}
                  </FormLabel>
                  <Select
                    inModal
                    isSearchable
                    placeholder={t('dashboard.team.choose-team')}
                    value={sourceTeamId}
                    setValue={(value) => {
                      setSourceTeamId(value)
                      setSourceError(false)
                    }}
                    options={(guild?.teamReferences ?? []).map((team) => ({
                      value: String(team.id),
                      label: team.name,
                    }))}
                  />
                  {sourceError && (
                    <p className='text-sm font-medium text-destructive'>
                      {t('dashboard.team.choose-team')}
                    </p>
                  )}
                </div>
              )}

              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='grow'>
                    <FormLabel>Team name</FormLabel>
                    <FormControl>
                      <Input placeholder='Team name' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <GenericFormError customError={error} />
            </form>
          </Form>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Cancel
            </Button>
          </DialogClose>
          {mode ? (
            <Button
              type='submit'
              form='new-team-form'
              loading={submitting}
              disabled={teamLimitReached}
            >
              {mode === 'duplicate'
                ? t('dashboard.team.create-options.duplicate-title')
                : 'Create'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NewTeamModal
